import React, { useState, useMemo } from 'react';
import { Power } from 'lucide-react';
import { tradingApi, SystemControl } from '../services/api';

interface SystemControlButtonProps {
    controlState: SystemControl | null;
    onRefresh: () => Promise<void>;
    className?: string;
    onError: (error: string) => void;
    variant?: 'compact' | 'slim';
    iconSize?: number;
}

const SystemControlButton: React.FC<SystemControlButtonProps> = ({
    controlState,
    onRefresh,
    className,
    onError,
    variant = 'compact',
    iconSize: iconSizeProp,
}) => {
    const [loading, setLoading] = useState(false);

    const buttonsTheme = useMemo(
        () => ({
            // Base styles common to all variants (colors, borders, transitions)
            base: "rounded-xl font-semibold transition-all duration-200 border",
            // Layout styles for Compact (Grid) view
            compact: "col-span-1 w-full px-3 py-3 flex flex-col items-center justify-center gap-1.5 text-xs sm:text-sm",
            // Layout styles for Slim (Row) view
            slim: "flex items-center justify-center gap-1.5 rounded-lg",

            start:
                "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border-emerald-500/20 shadow-xs",
            stop: "bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border-rose-500/20 shadow-xs",
            unavailable: "bg-white/5 text-muted border-white/10 shadow-none",
        }),
        []
    );

    const handleSystemToggle = async () => {
        if (!controlState) return;
        setLoading(true);
        try {
            const response = await tradingApi.setSystemEnabled(
                !controlState.system_enabled
            );
            if (response.success) {
                await onRefresh();
            } else {
                onError(response.message || "Failed to update system state");
            }
        } catch (err) {
            onError(
                err instanceof Error ? err.message : "Failed to update system state"
            );
        } finally {
            setLoading(false);
        }
    };

    if (!controlState) return null;

    const layoutClass = variant === 'compact' ? buttonsTheme.compact : buttonsTheme.slim;
    const isConnected = controlState.ib_connected;
    const colorClass = !isConnected
        ? buttonsTheme.unavailable
        : controlState.system_enabled
            ? buttonsTheme.stop
            : buttonsTheme.start;
    const actionLabel = !isConnected
        ? "Connect first"
        : controlState.system_enabled
            ? "Stop Trading"
            : "Start Trading";

    const iconSize = iconSizeProp ?? (variant === 'slim' ? 16 : 18);

    return (
        <button
            onClick={handleSystemToggle}
            disabled={loading || !isConnected}
            className={`${buttonsTheme.base} ${layoutClass} ${colorClass} ${className || ''} ${!isConnected ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
            title={!isConnected ? "Connect the broker before changing trading state" : controlState.system_enabled ? "Stop new trading activity" : "Start trading activity"}
            aria-label={actionLabel}
        >
            <Power size={iconSize} />
            <span className={variant === 'slim' ? 'min-w-0 truncate leading-none font-bold' : ''}>
                {actionLabel}
            </span>
        </button>
    );
};

export default SystemControlButton;
